import React, {
  Children,
  cloneElement,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import debounce from 'lodash.debounce';

/**
 * HEY LISTEN! Portal needs to be imported via the full path so that
 * Jest can pick up the automock!
 */
// eslint-disable-next-line no-restricted-imports
import Portal from 'next/components/ui/lib/Portal';

import Fade from './lib/Fade';

/**
 * NOTE: This component and the hook `usePosition` borrows heavily from the
 *       `Tether` implementation in the builder. However, the logic has been
 *       simplified greatly to only handle tethering to top/right/bottom/left
 *       center of the target and does not try to reposition if overflowing off
 *       screen. If we decide later that we need more robust tethering in
 *       studio, we can bring over the full implementation and refactor this
 *       component to leverage that abstraction. To note, `Dropdown` includes a
 *       nearly identical hook as well.
 */

/**
 * disabled elements do not trigger mouse events for its parent
 * unless pointer-events: none are added.
 */
const Wrapped = styled.div`
  display: inline-block;

  > * {
    &[disabled] {
      pointer-events: none;
    }
  }
`;

const Reset = styled.div`
  left: 0;
  position: fixed;
  top: 0;
`;

const Tip = styled.div`
  background: var(--tooltip-bg);
  border-color: var(--tooltip-bg);
  border-radius: 6px;
  color: var(--white);
  font-weight: var(--bold);
  font-size: var(--x-small);
  line-height: 1.5;
  padding: 10px 12px;
  position: absolute;
  text-align: center;
  white-space: normal;

  ::after {
    border: solid transparent;
    border-width: var(--tooltip-caret-width);
    content: '';
    display: inline-block;
    position: absolute;
  }

  ${({ size }) => {
    switch (size) {
      case 'l':
        return `width: 320px;`;
      case 'm':
        return `width: 200px;`;
      case 's':
        return `width: 120px;`;
      default:
        return `white-space: nowrap;`;
    }
  }}

  ${({ placement }) => {
    switch (placement) {
      case 'top':
        return `
        transform: translateY(calc(-1 * var(--tooltip-caret-width)));

        ::after {
          top: 100%;
          left: calc(50% - var(--tooltip-caret-width));
          border-top-color: inherit;
        }
      `;
      case 'right':
        return `
        transform: translateX(var(--tooltip-caret-width));

        ::after {
          right: 100%;
          top: calc(50% - var(--tooltip-caret-width));
          border-right-color: inherit;
        }
      `;
      case 'bottom':
        return `
        transform: translateY(var(--tooltip-caret-width));

        ::after {
          bottom: 100%;
          left: calc(50% - var(--tooltip-caret-width));
          border-bottom-color: inherit;
        }
      `;
      case 'left':
        return `
        transform: translateX(calc(-1 * var(--tooltip-caret-width)));

        ::after {
          left: 100%;
          top: calc(50% - var(--tooltip-caret-width));
          border-left-color: inherit;
        }
      `;
      default:
        return '';
    }
  }}

  ${({ persist }) => !persist && `pointer-events: none;`}
`;

/**
 * Safe helper to call `getBoundingClientRect` with fallback to zeros
 *
 * @param {?Element} $element - Element to get bounds
 * @return {DOMRect} Element's rect or zeros
 */
const getBoundingClientRect = $element =>
  $element
    ? $element.getBoundingClientRect()
    : { top: 0, right: 0, bottom: 0, left: 0, height: 0, width: 0 };

/**
 * Calculate all possible positions for attachment
 *
 * @param {object} params
 * @param {DOMRect} params.attachment - Attachment client rect
 * @param {DOMRect} params.target - Target client rect
 j @return {object} All possible top/left attachment positions
 */
const getPositions = ({ attachment, target }) => ({
  top: {
    top: target.top - attachment.height,
    left: target.left + target.width / 2 - attachment.width / 2,
  },
  right: {
    top: target.top + target.height / 2 - attachment.height / 2,
    left: target.right,
  },
  bottom: {
    top: target.bottom,
    left: target.left + target.width / 2 - attachment.width / 2,
  },
  left: {
    top: target.top + target.height / 2 - attachment.height / 2,
    left: target.left - attachment.width,
  },
});

/**
 * Hook to determine the absolute coordinates for tethering attachment to bottom
 * left or bottom right of the target
 *
 * @param {object} params
 * @param {Placement} params.placement - Placement of attachment
 * @param {boolean} params.visible - Whether the attachment is visible
 * @return {[object<React.RefObject>, object]} result - Refs and coordinates
 */
const usePosition = ({ placement, visible }) => {
  const target = useRef();
  const attachment = useRef();

  const [position, setPosition] = useState({});

  // Determine the top/left coordinates for tethered attachment based on the
  // position of the target
  const reposition = useCallback(() => {
    const { current: $target } = target;
    const { current: $attachment } = attachment;

    const targetRect = getBoundingClientRect($target);
    const attachmentRect = getBoundingClientRect($attachment);
    const positions = getPositions({
      attachment: attachmentRect,
      target: targetRect,
    });

    setPosition(positions[placement]);
  }, [placement]);

  // Bind scroll and resize event listeners to trigger repositioning
  useLayoutEffect(() => {
    // Skip binding handlers if the attachment is not visible
    if (!visible) {
      return;
    }

    // Create new debounced reposition handler (16ms ~ 60fps)
    const handler = debounce(reposition, 16, { leading: true });

    // Immediately reposition on next tick to force attachment to render
    setImmediate(reposition);

    window.addEventListener('resize', handler);
    window.addEventListener('scroll', handler);

    // eslint-disable-next-line consistent-return
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('scroll', handler);
    };
  }, [reposition, visible]);

  return [{ attachment, target }, position];
};

function Tooltip({
  children,
  className,
  delay = 100,
  disabled = false,
  label,
  persist = false,
  placement = 'top',
  size,
  timeout = 200,
  visible: forced,
  wrapped = false,
}) {
  const [open, setOpen] = useState(false);

  // If an explicit `visible` prop is provided, assume the tooltip is
  // managed by the parent and use that value over local state
  const visible = forced != null ? forced : open;

  const [refs, position] = usePosition({ placement, visible });

  // If disabled, render unwrapped children
  if (disabled) {
    return typeof children === 'function' ? children() : children;
  }

  const handleMouseOver = () => {
    setOpen(true);
  };

  const handleMouseOut = () => {
    setOpen(false);
  };

  const renderTarget = () => {
    const props = {
      onMouseOver: handleMouseOver,
      onMouseOut: handleMouseOut,
      ref: refs.target,
    };

    // Wrap the target if requested and needed
    if (wrapped) {
      return <Wrapped {...props}>{children}</Wrapped>;
    }

    // Assert that a single element is provided e.g. no fragments
    const child = Children.only(children);

    // Clone the element and pass mouse handlers
    return cloneElement(child, props);
  };

  return (
    <>
      {renderTarget()}

      <Portal timeout={delay + timeout}>
        <Reset>
          <Fade delay={delay} from="center" timeout={timeout} visible={visible}>
            <Tip
              className={className}
              placement={placement}
              ref={refs.attachment}
              role="tooltip"
              size={size}
              style={position}
              persist={persist}
              onMouseOver={handleMouseOver}
              onMouseOut={handleMouseOut}
            >
              {typeof label === 'function' ? label() : label}
            </Tip>
          </Fade>
        </Reset>
      </Portal>
    </>
  );
}

Tooltip.propTypes = {
  // Target component to tether tooltip
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),

  // Classname passthrough for styling tooltip
  className: PropTypes.string,

  // Delay before showing/hiding tooltip
  delay: PropTypes.number,

  // Whether to disable the tooltip
  disabled: PropTypes.bool,

  // Label to show in the tooltip
  label: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),

  // Whether to keep the tooltip visible if hovering over it
  persist: PropTypes.bool,

  // Tooltip placement
  placement: PropTypes.oneOf(['top', 'right', 'bottom', 'left']),

  // Preset sizes
  size: PropTypes.oneOf(['s', 'm', 'l']),

  // Duration of tooltip fade
  timeout: PropTypes.number,

  // Forced visible state
  visible: PropTypes.bool,

  // Whether the target needs to be wrapped
  wrapped: PropTypes.bool,
};

export default styled(Tooltip)``;
